import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wand2, RefreshCw, Save, Layers, ShieldCheck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';

// ----- helpers ---------------------------------------------------------------
let __uidCounter = 0;
const nextUid = () => `aiitem-${++__uidCounter}`;

function makeKeyFor(idCache) {
  return (cacheKey, item, fallbackIdx) => {
    if (item && typeof item === 'object') {
      const cache = idCache[cacheKey];
      if (!cache.has(item)) cache.set(item, nextUid());
      return cache.get(item);
    }
    return `${cacheKey}-${fallbackIdx}`;
  };
}

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

// ----- subcomponents ---------------------------------------------------------
function PanelHeader({ ai, mode, original, view, setView }) {
  return (
    <CardHeader className="pb-3 border-b bg-white/70 backdrop-blur rounded-t-xl">
      <div className="flex flex-wrap items-center gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wand2 className="h-4 w-4 text-primary" /> AI Recovery Recommendations
        </CardTitle>
        <Badge variant="secondary" className="text-xs">
          <Layers className="h-3 w-3 mr-1" />
          {ai?.source_used ? `Grounded: ${ai.source_used}` : 'No grounding yet'}
        </Badge>
        <Badge variant="outline" className="text-xs uppercase">{mode}</Badge>
        <Badge variant="outline" className="text-xs text-primary border-primary/40" data-testid="ai-deidentified-badge">
          <ShieldCheck className="h-3 w-3 mr-1" /> PHI stripped before AI
        </Badge>
        <div className="flex-1" />
        {original && (
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-7">
              <TabsTrigger value="current" className="text-xs h-6" data-testid="ai-view-current">Current</TabsTrigger>
              <TabsTrigger value="original" className="text-xs h-6" data-testid="ai-view-original">Original AI</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>
    </CardHeader>
  );
}

function RestrictionsList({ items, readOnly, keyFor, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">Restrictions</label>
        {!readOnly && <Button size="sm" variant="ghost" onClick={onAdd} data-testid="ai-add-restriction">+ Add</Button>}
      </div>
      <div className="space-y-2 mt-2">
        {items.map((r, i) => (
          /* Phone: category on its own line, then limit | weeks | remove with fixed widths so the
             number box and the × stay tappable. From sm: the original 3/7/1/1 twelve-column row. */
          <div key={keyFor('restrictions', r, i)} className="grid grid-cols-[minmax(0,1fr)_4rem_2.75rem] sm:grid-cols-12 gap-2 items-center">
            <Input className="col-span-3 sm:col-span-3" readOnly={readOnly} value={r.category || ''} onChange={(e) => onChange(i, 'category', e.target.value)} placeholder="category" data-testid={`ai-restriction-cat-${i}`} />
            <Input className="sm:col-span-7" readOnly={readOnly} value={r.limit || ''} onChange={(e) => onChange(i, 'limit', e.target.value)} placeholder="limit" data-testid={`ai-restriction-limit-${i}`} />
            <Input className="sm:col-span-1 px-2" readOnly={readOnly} value={r.duration_weeks ?? ''} type="number" onChange={(e) => onChange(i, 'duration_weeks', parseInt(e.target.value || '0'))} placeholder="wk" data-testid={`ai-restriction-weeks-${i}`} />
            {!readOnly && <Button size="icon" variant="ghost" className="sm:col-span-1 h-11 w-11" onClick={() => onRemove(i)} aria-label="Remove restriction">×</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineList({ items, readOnly, keyFor, onChange }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">Recovery Timeline</label>
      <div className="space-y-2 mt-2">
        {items.map((t, i) => (
          <div key={keyFor('timeline', t, i)} className="grid grid-cols-12 gap-2">
            <Input className="col-span-3" readOnly={readOnly} value={t.period || ''} onChange={(e) => onChange(i, 'period', e.target.value)} />
            <Input className="col-span-9" readOnly={readOnly} value={t.milestones || ''} onChange={(e) => onChange(i, 'milestones', e.target.value)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DosDontsList({ groupKey, label, items, readOnly, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        {!readOnly && <Button size="sm" variant="ghost" onClick={() => onAdd(groupKey)} data-testid={`ai-add-${groupKey}`}>+ Add</Button>}
      </div>
      <div className="space-y-1 mt-2">
        {items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key -- primitive string list; remount-on-edit is acceptable for short curated lists
          <div key={`${groupKey}-${i}`} className="flex gap-2 items-center">
            <Input readOnly={readOnly} value={item} onChange={(e) => onChange(groupKey, i, e.target.value)} data-testid={`ai-${groupKey}-${i}`} />
            {!readOnly && <Button size="sm" variant="ghost" onClick={() => onRemove(groupKey, i)} aria-label="Remove item">×</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function GenerateControls({ doctorNote, setDoctorNote, onGenerate, busy, hasAI }) {
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2">
      <Textarea
        value={doctorNote}
        onChange={(e) => setDoctorNote(e.target.value)}
        placeholder="(Optional) Doctor note to refine output — e.g. 'Emphasize gradual standing return; patient is a warehouse supervisor.'"
        rows={2}
        data-testid="ai-doctor-note"
      />
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => onGenerate({ doctor_note: doctorNote })} disabled={busy} data-testid="doctor-ai-generate-button">
          <Wand2 className="h-4 w-4 mr-1" /> {hasAI ? 'Regenerate' : 'Generate'}
        </Button>
        <Button variant="outline" onClick={() => onGenerate({ doctor_note: doctorNote, mode: 'mock' })} disabled={busy} data-testid="doctor-ai-mock-button">
          <RefreshCw className="h-4 w-4 mr-1" /> Generate with Mock
        </Button>
        <Button variant="outline" onClick={() => onGenerate({ doctor_note: doctorNote, mode: 'real' })} disabled={busy} data-testid="doctor-ai-real-button">
          <Save className="h-4 w-4 mr-1" /> Generate with Real AI
        </Button>
      </div>
    </div>
  );
}

// ----- main component --------------------------------------------------------
export function AIPanel({ ai, original, mode = 'real', onGenerate, onChange, busy }) {
  const [doctorNote, setDoctorNote] = useState('');
  const [view, setView] = useState('current');

  const idCacheRef = useRef({
    restrictions: new WeakMap(),
    timeline: new WeakMap(),
  });
  const keyFor = makeKeyFor(idCacheRef.current);

  const display = useMemo(
    () => (view === 'original' ? original || ai : ai),
    [view, original, ai],
  );
  const readOnly = view === 'original';

  const update = useCallback((path, value) => {
    const next = clone(ai);
    const segs = path.split('.');
    let cur = next;
    for (let i = 0; i < segs.length - 1; i++) {
      if (!cur[segs[i]]) cur[segs[i]] = {};
      cur = cur[segs[i]];
    }
    cur[segs[segs.length - 1]] = value;
    onChange(next);
  }, [ai, onChange]);

  const updateRestriction = useCallback((i, key, value) => {
    const next = clone(ai);
    next.restrictions = next.restrictions || [];
    next.restrictions[i] = { ...(next.restrictions[i] || {}), [key]: value };
    onChange(next);
  }, [ai, onChange]);

  const addRestriction = useCallback(() => {
    const next = clone(ai);
    next.restrictions = next.restrictions || [];
    next.restrictions.push({ category: 'other', limit: '', duration_weeks: 4 });
    onChange(next);
  }, [ai, onChange]);

  const removeRestriction = useCallback((i) => {
    const next = clone(ai);
    next.restrictions = (next.restrictions || []).filter((_, idx) => idx !== i);
    onChange(next);
  }, [ai, onChange]);

  const updateTimeline = useCallback((i, key, value) => {
    const next = clone(ai);
    next.timeline = next.timeline || [];
    next.timeline[i] = { ...(next.timeline[i] || {}), [key]: value };
    onChange(next);
  }, [ai, onChange]);

  const addListItem = useCallback((key) => {
    const next = clone(ai);
    next.dos_and_donts = next.dos_and_donts || { dos: [], donts: [] };
    next.dos_and_donts[key] = next.dos_and_donts[key] || [];
    next.dos_and_donts[key].push('');
    onChange(next);
  }, [ai, onChange]);

  const updateListItem = useCallback((key, i, value) => {
    const next = clone(ai);
    next.dos_and_donts = next.dos_and_donts || { dos: [], donts: [] };
    next.dos_and_donts[key][i] = value;
    onChange(next);
  }, [ai, onChange]);

  const removeListItem = useCallback((key, i) => {
    const next = clone(ai);
    next.dos_and_donts = next.dos_and_donts || { dos: [], donts: [] };
    next.dos_and_donts[key] = next.dos_and_donts[key].filter((_, idx) => idx !== i);
    onChange(next);
  }, [ai, onChange]);

  return (
    <Card className="border-[hsl(174_35%_80%)] bg-[hsl(210_40%_98%)]" data-testid="ai-panel">
      <PanelHeader ai={ai} mode={mode} original={original} view={view} setView={setView} />
      <CardContent className="pt-4 space-y-4">
        {!ai ? (
          <div className="text-sm text-muted-foreground rounded-lg border border-dashed p-6 text-center">
            No recommendations yet. Use “Generate” below.
          </div>
        ) : (
          <>
            {display?._warning && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="ai-mock-fallback-warning"
              >
                <span className="font-semibold">Not real AI output.</span> The model call failed and
                deterministic mock content was substituted, so these recommendations are template text
                rather than a reading of this patient. Previously this was announced only in a toast,
                which vanished and left the panel looking like genuine AI output.
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Summary</label>
              <Textarea readOnly={readOnly} value={display?.summary || ''} onChange={(e) => update('summary', e.target.value)} rows={3} data-testid="ai-summary" />
            </div>

            <RestrictionsList
              items={display?.restrictions || []}
              readOnly={readOnly}
              keyFor={keyFor}
              onChange={updateRestriction}
              onAdd={addRestriction}
              onRemove={removeRestriction}
            />

            <TimelineList
              items={display?.timeline || []}
              readOnly={readOnly}
              keyFor={keyFor}
              onChange={updateTimeline}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['dos', "Do's"], ['donts', "Don'ts"]].map(([k, label]) => (
                <DosDontsList
                  key={k}
                  groupKey={k}
                  label={label}
                  items={display?.dos_and_donts?.[k] || []}
                  readOnly={readOnly}
                  onChange={updateListItem}
                  onAdd={addListItem}
                  onRemove={removeListItem}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Follow-up</label>
                <Input readOnly={readOnly} value={display?.follow_up || ''} onChange={(e) => update('follow_up', e.target.value)} data-testid="ai-followup" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Return-to-work estimate (weeks)</label>
                <Input readOnly={readOnly} type="number" value={display?.return_to_work_estimate_weeks ?? ''} onChange={(e) => update('return_to_work_estimate_weeks', parseInt(e.target.value || '0'))} data-testid="ai-rtw-weeks" />
              </div>
            </div>
          </>
        )}

        <GenerateControls
          doctorNote={doctorNote}
          setDoctorNote={setDoctorNote}
          onGenerate={onGenerate}
          busy={busy}
          hasAI={!!ai}
        />
      </CardContent>
    </Card>
  );
}
